import {useState, useEffect, useRef} from "react";
import {tap} from "ramda";
// @ts-ignore
import StoryblokClient from "storyblok-js-client";

import storyblokConfig from "./storyblokConfig";
import {GetStoryLanguage} from "../../src/helpers/storyHelpers";
import {getAuthorBySlug} from "../../src/api/authors/requests";
import {getArticleBySlug} from "../../src/api/articles/requests";
import {getAboutPage} from "../../src/api/about/requests";
import {getCategoryBySlug} from "../../src/api/category/requests";
import {getSearchResultsPage} from "../../src/api/search/requests";
import {getTagStory} from "../../src/api/tag/requests";
import {getHomepage} from "../../src/api/home/requests";
import {withNotFound} from "../../src/features/notFound/store";
import {trackArticleRead} from "../../src/features/articles/analytics";
import {setIsNotFound} from "../../src/features/notFound/store";
import {STATUS_NOT_FOUND} from "../../src/constants/httpStatusCodes";

let sbClient = new StoryblokClient({
  accessToken: storyblokConfig.devToken,
});

function loadBridge(callback) {
  const existingScript = document.getElementById("storyblokBridge");
  if (!existingScript) {
    const script = document.createElement("script");
    script.src = "//app.storyblok.com/f/storyblok-v2-latest.js";
    script.id = "storyblokBridge";
    document.body.appendChild(script);
    script.onload = () => {
      callback();
    };
  } else {
    callback();
  }
}

function parseStory(story) {
  if (typeof story.content === "string") {
    let newStory = {
      ...story,
      content: JSON.parse(story.content),
    };
    return newStory;
  }
  return story;
}

function RefUseStoryblok(newSlug) {
  const [story, setStory] = useState({});
  const slug = useRef();

  useEffect(() => {
    slug.current = newSlug;
    if (window.location.search.includes("_storyblok")) {
      loadBridge(() => {
        const {StoryblokBridge, location} = window;
        const storyblokInstance = new StoryblokBridge();

        storyblokInstance.on(["published", "change"], event => {
          if (!event.slugChanged) {
            location.reload();
          }
        });

        storyblokInstance.on("input", event => {
          // Access currently changed but not yet saved content via:
          const s = parseStory(event.story);
          s.lang = GetStoryLanguage(s);
          setStory(s);
        });

        storyblokInstance.on("enterEditmode", event => {
          let params = new URLSearchParams(window.location.search);
          // loading the draft version on initial enter of editor
          sbClient
            .get(`cdn/stories/${event.storyId}`, {
              version: "draft",
              language: params.get("_storyblok_lang")
                ? params.get("_storyblok_lang")
                : "en",
            })
            .then(({data}) => {
              let newStory = parseStory(data.story);
              if (story.field_component && !newStory.field_component) {
                newStory.field_component = story.field_component;
              }
              newStory.lang = GetStoryLanguage(newStory);
              setStory(newStory);
            })
            .catch(error => {
              console.error(error);
            });
        });
      });
    } else if (newSlug) {
      // No bridge, just get page?
      GetStory(newSlug).then(story => {
        setStory(story);
      });

      return;
    }

    if (typeof story.content === "string") {
      setStory(parseStory(story));
    }
  }, [newSlug]);

  return slug.current !== newSlug ? {} : story;
}

function GetStory(slug) {
  if (slug === "/") {
    return withNotFound(getHomepage);
  }

  if (slug.startsWith("/author")) {
    return withNotFound(getAuthorBySlug, slug);
  }

  if (slug.startsWith("/about")) {
    return withNotFound(getAboutPage);
  }

  if (slug.startsWith("/search")) {
    return withNotFound(getSearchResultsPage);
  }

  if (slug.startsWith("/tag/")) {
    return withNotFound(getTagStory);
  }

  if (slug.split("/").filter(Boolean).length > 1) {
    return getArticleBySlug(slug)
      .then(tap(trackArticleRead))
      .catch(error => {
        setIsNotFound(false);

        if (error.status === STATUS_NOT_FOUND) {
          setIsNotFound(true);
        }

        return error;
      });
  }

  return withNotFound(getCategoryBySlug, slug);
}

export default RefUseStoryblok;
